import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import blogPostsData from '../data/blogPosts.json'; // Renamed to avoid conflict
import Header from '../components/Header';

const POSTS_PER_LOAD = 6; // Number of posts to load at a time

export default function BlogIndex() {
  const [activeCategory, setActiveCategory] = React.useState('all');
  const [postsToShow, setPostsToShow] = React.useState(POSTS_PER_LOAD);

  const locale = sessionStorage.getItem('versant_locale') || 'en';

  const translations = {
    en: {
      metaTitle: "Blog - Versant Test Prep Insights",
      metaDesc: "Discover expert insights, tips, and strategies to improve your English proficiency for the Versant Test and drive more organic traffic.",
      hTitle: "Our Blog",
      hDesc: "Discover expert insights, tips, and strategies to boost your English proficiency and ace your Versant Test.",
      featured: "Featured Article",
      readMore: "Read Full Article",
      latest: "Latest Articles",
      loadMore: "Load More Articles",
      newsletterTitle: "Stay Updated with Versant Insights",
      newsletterDesc: "Get the latest Versant tips and strategies delivered directly to your inbox.",
      newsletterPlace: "Enter your email address",
      subscribe: "Subscribe",
      subscribed: "Thank you for subscribing!",
      catTitle: "Blog Categories",
      popularTitle: "Popular Posts",
      resources: "Resources",
      follow: "Follow Us",
      allPosts: "All Posts",
      readMoreArrow: "Read More →",
      aboutUs: "About Us",
      contactUs: "Contact Us",
      privacy: "Privacy Policy",
      terms: "Terms & Conditions"
    },
    es: {
      metaTitle: "Blog - Consejos de Preparación para Versant",
      metaDesc: "Descubra consejos de expertos, sugerencias y estrategias para mejorar su nivel de inglés para la prueba Versant.",
      hTitle: "Nuestro Blog",
      hDesc: "Descubra consejos de expertos y estrategias para mejorar su nivel de inglés y aprobar su examen Versant.",
      featured: "Artículo Destacado",
      readMore: "Leer Artículo Completo",
      latest: "Últimos Artículos",
      loadMore: "Cargar Más Artículos",
      newsletterTitle: "Mantente al Día con Versant Insights",
      newsletterDesc: "Recibe los últimos consejos de Versant directamente en tu bandeja de entrada.",
      newsletterPlace: "Introduce tu dirección de correo electrónico",
      subscribe: "Suscribirse",
      subscribed: "¡Gracias por suscribirte!",
      catTitle: "Categorías del Blog",
      popularTitle: "Entradas Populares",
      resources: "Recursos",
      follow: "Síguenos",
      allPosts: "Todos los Artículos",
      readMoreArrow: "Leer Más →",
      aboutUs: "Sobre Nosotros",
      contactUs: "Contacto",
      privacy: "Política de Privacidad",
      terms: "Términos y Condiciones"
    },
    pt: {
      metaTitle: "Blog - Dicas de Preparação para o Versant",
      metaDesc: "Descubra dicas de especialistas e estratégias para melhorar sua proficiência em inglês para o Teste Versant.",
      hTitle: "Nosso Blog",
      hDesc: "Descubra dicas de especialistas e estratégias para aumentar sua proficiência em inglês e passar no Teste Versant.",
      featured: "Artigo em Destaque",
      readMore: "Ler Artigo Completo",
      latest: "Últimos Artigos",
      loadMore: "Carregar Mais Artigos",
      newsletterTitle: "Fique Atualizado com Versant Insights",
      newsletterDesc: "Receba as últimas dicas e estratégias do Versant direto na sua caixa de entrada.",
      newsletterPlace: "Digite seu endereço de e-mail",
      subscribe: "Inscrever-se",
      subscribed: "Obrigado por se inscrever!",
      catTitle: "Categorias do Blog",
      popularTitle: "Artigos Populares",
      resources: "Recursos",
      follow: "Siga-nos",
      allPosts: "Todos os Artigos",
      readMoreArrow: "Ler Mais →",
      aboutUs: "Sobre Nós",
      contactUs: "Contato",
      privacy: "Política de Privacidade",
      terms: "Termos e Condições"
    },
    ja: {
      metaTitle: "ブログ - Versant対策コラム",
      metaDesc: "Versantテストで高得点を取得するための専門的な対策、コツ、学習戦略を解説します。",
      hTitle: "対策コラム・ブログ",
      hDesc: "Versantテストのスコアアップや英語力向上のための、専門家による学習のヒントと戦略を紹介します。",
      featured: "おすすめの記事",
      readMore: "記事を読む",
      latest: "最新の記事一覧",
      loadMore: "もっと記事を表示する",
      newsletterTitle: "最新のVersant対策情報をお届け",
      newsletterDesc: "試験のコツや学習のアドバイスをメールマガジンで定期的にお届けします。",
      newsletterPlace: "メールアドレスを入力してください",
      subscribe: "購読する",
      subscribed: "購読登録が完了しました！",
      catTitle: "カテゴリー",
      popularTitle: "人気の記事",
      resources: "リンク集",
      follow: "ソーシャルメディア",
      allPosts: "すべての記事",
      readMoreArrow: "詳しく見る →",
      aboutUs: "会社概要",
      contactUs: "お問い合わせ",
      privacy: "プライバシーポリシー",
      terms: "利用規約"
    }
  };

  const t = translations[locale] || translations.en;

  const { featuredPost, otherPosts, categories } = React.useMemo(() => {
    const sortedBlogPosts = [...blogPostsData].sort((a, b) => new Date(b.date) - new Date(a.date));
    const featured = sortedBlogPosts.length > 0 ? sortedBlogPosts[0] : null;
    const others = sortedBlogPosts.slice(1);
    const cats = ['all', ...new Set(blogPostsData.map(post => post.category))];
    return { featuredPost: featured, otherPosts: others, categories: cats };
  }, []);

  const filteredPosts = React.useMemo(() => {
    let posts = otherPosts;
    if (activeCategory !== 'all') {
      posts = otherPosts.filter(post => post.category === activeCategory);
    }
    return posts;
  }, [activeCategory, otherPosts]);

  const postsToDisplay = React.useMemo(() => {
    return filteredPosts.slice(0, postsToShow);
  }, [filteredPosts, postsToShow]);

  const handleLoadMore = React.useCallback(() => {
    setPostsToShow(prev => prev + POSTS_PER_LOAD);
  }, []);

  const handleNewsletterSignup = React.useCallback((event) => {
    event.preventDefault();
    const email = event.target.querySelector('input[type="email"]').value;
    alert(`${t.subscribed} (${email})`);
  }, [t.subscribed]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <meta name="keywords" content="Versant, blog, English test, test prep, language learning, SEO" />
      </Helmet>
      <Header />

      {/* Header Section */}
      <header className="gradient-bg text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4">{t.hTitle}</h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">{t.hDesc}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12 flex-grow">
        {/* Featured Post */}
        {featuredPost && (
          <section className="mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">{t.featured}</h2>
            <article className="bg-white rounded-2xl shadow-xl overflow-hidden blog-card">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img src={`${process.env.PUBLIC_URL}${featuredPost.imageUrl}`} alt={featuredPost.title} className="h-64 md:h-full w-full object-cover" />
                </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <div className="flex items-center mb-4">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">{featuredPost.category}</span>
                    <span className="text-gray-500 ml-4 text-sm">{featuredPost.date} by {featuredPost.author}</span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                    <Link to={`/blog/${featuredPost.slug}`} className="hover:text-green-700 transition duration-300">
                      {featuredPost.title}
                    </Link>
                  </h3>
                  <p className="text-gray-700 text-lg mb-6 line-clamp-3">{featuredPost.excerpt}</p>
                  <Link to={`/blog/${featuredPost.slug}`} className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300 self-start">
                    {t.readMore}
                  </Link>
                </div>
              </div>
            </article>
          </section>
        )}

        {/* Categories Filter */}
        <section className="mb-12">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => {
                  setActiveCategory(category);
                  setPostsToShow(POSTS_PER_LOAD);
                }}
                className={`tag bg-white border-2 ${activeCategory === category ? 'border-green-600 text-green-600' : 'border-gray-300 text-gray-600'} px-6 py-2 rounded-full font-medium hover:border-green-600 hover:text-green-600 transition-all duration-300`}
              >
                {category === 'all' ? t.allPosts : category}
              </button>
            ))}
          </div>
        </section>

        {/* Blog Grid */}
        <section>
          <h2 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">{t.latest}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" id="blogGrid">
            {postsToDisplay.map(post => (
              <article key={post.id} className="blog-card bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
                <img src={`${process.env.PUBLIC_URL}${post.imageUrl}`} alt={post.title} className="h-56 w-full object-cover" />
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center mb-3">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">{post.category}</span>
                    <span className="text-gray-500 ml-auto text-sm">{post.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight line-clamp-2 flex-grow">
                    <Link to={`/blog/${post.slug}`} className="hover:text-green-700 transition duration-300">
                      {post.title}
                    </Link>
                  </h3>
                  <p className="text-gray-650 mb-4 line-clamp-3">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`} className="text-green-600 font-medium hover:text-green-800 transition-colors duration-300 mt-auto">
                    {t.readMoreArrow}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Load More Button */}
        {postsToDisplay.length < filteredPosts.length && (
          <div className="text-center mt-12">
            <button
              onClick={handleLoadMore}
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-green-700 transition-all duration-300 shadow-lg"
            >
              {t.loadMore}
            </button>
          </div>
        )}
      </main>

      {/* Newsletter Signup */}
      <section className="bg-gray-800 text-white py-16 mt-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">{t.newsletterTitle}</h2>
          <p className="text-xl text-gray-300 mb-8">{t.newsletterDesc}</p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" onSubmit={handleNewsletterSignup}>
            <input type="email" placeholder={t.newsletterPlace} className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500" required />
            <button type="submit" className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-medium transition-colors duration-300">
              {t.subscribe}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">{t.catTitle}</h3>
              <ul className="space-y-2 text-gray-300">
                {categories.filter(cat => cat !== 'all').map(cat => (
                  <li key={cat}><button onClick={() => {
                    setActiveCategory(cat);
                    setPostsToShow(POSTS_PER_LOAD);
                  }} className="hover:text-white transition-colors">{cat}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">{t.popularTitle}</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/blog/pearson-versant-test-practice-prep-guide" className="hover:text-white transition-colors">Mastering the Versant Test</Link></li>
                <li><Link to="/blog/versant-listening-mistakes" className="hover:text-white transition-colors">5 Common Mistakes</Link></li>
                <li><Link to="/blog/versant-listening-complete-guide" className="hover:text-white transition-colors">Improve Pronunciation</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">{t.resources}</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/about" className="hover:text-white transition-colors">{t.aboutUs}</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">{t.contactUs}</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t.privacy}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t.terms}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">{t.follow}</h3>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-300 hover:text-white transition-colors"><i className="fab fa-twitter"></i></a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors"><i className="fab fa-linkedin"></i></a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors"><i className="fab fa-facebook"></i></a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Versant Test. All rights reserved. | Designed for English Proficiency</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
